/**
 * Custom revision sorting utility.
 * Sort order: Single Letters (A-Z) -> Numbers (1-999) -> Double Letters (AA-ZZ)
 */

/**
 * "DE" cycle: the A/B/C/0/1 cycle finishes first, then the DE cycle starts
 * (DEA, DEB, DEC, DE0, DE1...). Accepts "DE0", "DE-0" and "DE 0".
 */
export const CYCLE_PREFIX_RE = /^(DE)[-_ ]?(?=[A-Z0-9])/;
const CYCLE_STAGE_STEP = 10;

export function splitCyclePrefix(rev) {
  const match = CYCLE_PREFIX_RE.exec(rev);
  if (match) {
    const base = rev.slice(match[0].length);
    if (/^([A-Z]|\d+|[A-Z]{2,}|[A-Z]+\d+)$/.test(base)) return [1, base];
  }
  return [0, rev];
}

export function revisionSortKey(revision) {
  if (!revision) return [999, 0];
  let rev = revision.trim().toUpperCase();
  rev = rev.replace(/^(REV\.?\s*|R\s+)/i, '').trim();
  if (!rev) return [999, 0];
  const [stage, base] = splitCyclePrefix(rev);
  const offset = stage * CYCLE_STAGE_STEP;
  rev = base;
  if (/^[A-Z]$/.test(rev)) return [offset + 0, rev.charCodeAt(0) - 65];
  if (/^\d+$/.test(rev)) return [offset + 1, parseInt(rev, 10)];
  if (/^[A-Z]{2}$/.test(rev)) return [offset + 2, (rev.charCodeAt(0) - 65) * 26 + (rev.charCodeAt(1) - 65)];
  if (/^[A-Z]{3,}$/.test(rev)) {
    let val = 0;
    for (let i = 0; i < rev.length; i++) val = val * 26 + (rev.charCodeAt(i) - 65);
    return [offset + 3, val];
  }
  const mixed = /^([A-Z]+)(\d+)$/.exec(rev);
  if (mixed) {
    let letterVal = 0;
    for (let i = 0; i < mixed[1].length; i++) letterVal = letterVal * 26 + (mixed[1].charCodeAt(i) - 65);
    return [offset + 4, letterVal * 1000 + Math.min(parseInt(mixed[2], 10), 999)];
  }
  return [999, 0];
}

export function compareRevisions(a, b) {
  const keyA = revisionSortKey(a);
  const keyB = revisionSortKey(b);
  if (keyA[0] !== keyB[0]) return keyA[0] - keyB[0];
  return keyA[1] - keyB[1];
}

export function sortByRevision(items, field = 'revision', descending = false) {
  return [...items].sort((a, b) => {
    const cmp = compareRevisions(a[field], b[field]);
    return descending ? -cmp : cmp;
  });
}

export function getLatestRevision(items, field = 'revision') {
  if (!items || items.length === 0) return null;
  return items.reduce((latest, item) => {
    return compareRevisions(item[field], latest[field]) > 0 ? item : latest;
  }, items[0]);
}
